import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchTabsStore, normalizeSearch } from '@/store/searchTabsStore';
import { useRecentSearchesStore } from '@/store/recentSearchesStore';
import { buildSearchTabTitle } from '@/utils/searchTabTitle';
import {
  getSearchTabIdentity,
  rememberSearchDemoTabId,
  searchHasAppliedFilters,
  searchHasDemoTour,
  stripDemoTourParams,
} from '@/utils/searchTabIdentity';
import { applyMappingStateFromSearch } from '@/utils/mappingUrlState';
import { isSearchTabsEnabled } from '@/config/searchTabs';
import { takeSearchDemoTabId } from '@/utils/searchTabIdentity';

export type NavigateSearchOptions = {
  search: string;
  title?: string;
  openInNewTab?: boolean;
  searchItems?: any[] | null;
  pathname?: string;
};

type TabActionResult = { ok: true } | { ok: false; reason: 'max_tabs' | 'not_found' };

let pendingProgrammaticSearch: string | null = null;

function beginProgrammaticNavigation(search: string) {
  pendingProgrammaticSearch = normalizeSearch(search);
  window.setTimeout(() => {
    if (pendingProgrammaticSearch === normalizeSearch(search)) {
      pendingProgrammaticSearch = null;
    }
  }, 1000);
}

export function isProgrammaticSearchNavigationPending(): boolean {
  return pendingProgrammaticSearch != null;
}

function tabSearchForNavigation(search: string): string {
  return stripDemoTourParams(search);
}

function resolveTitle(options: NavigateSearchOptions): string {
  if (options.title?.trim()) return options.title.trim();
  return buildSearchTabTitle(options.searchItems, options.search);
}

function prepareSearchDestination(search: string) {
  beginProgrammaticNavigation(search);
  applyMappingStateFromSearch(search);
}

function findReusableTab(search: string) {
  const identity = getSearchTabIdentity(search);
  const incomingHasFilters = searchHasAppliedFilters(search);
  return useSearchTabsStore.getState().tabs.find(tab => {
    if (getSearchTabIdentity(tab.search) !== identity) return false;
    const tabHasFilters = searchHasAppliedFilters(tab.search);
    // A clean search must not steal a filtered tab of the same item.
    if (!incomingHasFilters) return !tabHasFilters;
    return normalizeSearch(tab.search) === normalizeSearch(search);
  });
}

export function useSearchTabNavigation() {
  const navigate = useNavigate();

  const openTab = useSearchTabsStore(s => s.openTab);
  const replaceActiveTab = useSearchTabsStore(s => s.replaceActiveTab);
  const activateTab = useSearchTabsStore(s => s.activateTab);
  const closeTab = useSearchTabsStore(s => s.closeTab);
  const duplicateTab = useSearchTabsStore(s => s.duplicateTab);
  const reorderTabs = useSearchTabsStore(s => s.reorderTabs);
  const syncActiveFromUrl = useSearchTabsStore(s => s.syncActiveFromUrl);
  const flushActiveSearch = useSearchTabsStore(s => s.flushActiveSearch);
  const resetToSingleTab = useSearchTabsStore(s => s.resetToSingleTab);
  const tabs = useSearchTabsStore(s => s.tabs);
  const activeTabId = useSearchTabsStore(s => s.activeTabId);
  const maxTabs = useSearchTabsStore(s => s.maxTabs);

  const navigateSearch = useCallback(
    (options: NavigateSearchOptions): TabActionResult => {
      const pathname = options.pathname ?? '/search';
      const search = tabSearchForNavigation(options.search);
      const title = resolveTitle(options);

      // Persist filters/mapping from replaceState before leaving the current tab.
      flushActiveSearch(window.location.search);

      if (pathname === '/search') {
        useRecentSearchesStore.getState().remember(search, title);
      }

      if (isSearchTabsEnabled) {
        const existing = findReusableTab(search);
        if (existing) {
          const destination = tabSearchForNavigation(existing.search);
          activateTab(existing.id);
          prepareSearchDestination(destination);
          navigate({ pathname, search: destination });
          return { ok: true };
        }

        const opened = openTab({ search, title });
        if (!opened.ok) {
          if (options.openInNewTab) {
            return opened;
          }
          replaceActiveTab({ search, title });
        }
      } else {
        replaceActiveTab({ search, title });
      }

      prepareSearchDestination(search);
      navigate({ pathname, search });
      return { ok: true };
    },
    [activateTab, flushActiveSearch, navigate, openTab, replaceActiveTab],
  );

  const switchToTab = useCallback(
    (id: string) => {
      const tab = useSearchTabsStore.getState().tabs.find(t => t.id === id);
      if (!tab) return;

      flushActiveSearch(window.location.search);

      const search = tabSearchForNavigation(tab.search);
      useRecentSearchesStore.getState().remember(search, tab.title);
      activateTab(id);
      prepareSearchDestination(search);
      navigate({ pathname: '/search', search });
    },
    [activateTab, flushActiveSearch, navigate],
  );

  const closeSearchTab = useCallback(
    (id: string, blankSearchFactory: () => { search: string; title: string }) => {
      flushActiveSearch(window.location.search);
      const { nextActiveId, closedActive } = closeTab(id);

      if (!closedActive) return;

      if (!nextActiveId) {
        const blank = blankSearchFactory();
        const search = tabSearchForNavigation(blank.search);
        replaceActiveTab(blank);
        prepareSearchDestination(search);
        navigate({ pathname: '/search', search });
        return;
      }

      const next = useSearchTabsStore.getState().tabs.find(t => t.id === nextActiveId);
      if (!next) return;
      const search = tabSearchForNavigation(next.search);
      prepareSearchDestination(search);
      navigate({ pathname: '/search', search });
    },
    [closeTab, flushActiveSearch, navigate, replaceActiveTab],
  );

  const duplicateSearchTab = useCallback(
    (id: string): TabActionResult => {
      flushActiveSearch(window.location.search);
      const result = duplicateTab(id);
      if (!result.ok) return result;

      const copy = useSearchTabsStore.getState().tabs.find(t => t.id === result.id);
      if (!copy) return { ok: false, reason: 'not_found' };

      const search = tabSearchForNavigation(copy.search);
      prepareSearchDestination(search);
      navigate({ pathname: '/search', search });
      return { ok: true };
    },
    [duplicateTab, flushActiveSearch, navigate],
  );

  const closeAllSearchTabs = useCallback(
    (blankSearchFactory: () => { search: string; title: string }) => {
      const blank = blankSearchFactory();
      const search = tabSearchForNavigation(blank.search);
      resetToSingleTab(blank);
      prepareSearchDestination(search);
      navigate({ pathname: '/search', search });
    },
    [navigate, resetToSingleTab],
  );

  const openAllResultsSearch = useCallback(
    (blankSearchFactory: () => { search: string; title: string }) => {
      const blank = blankSearchFactory();
      return navigateSearch({
        search: blank.search,
        title: blank.title,
      });
    },
    [navigateSearch],
  );

  const reorderSearchTabs = useCallback(
    (fromIndex: number, toIndex: number) => reorderTabs(fromIndex, toIndex),
    [reorderTabs],
  );

  const syncFromLocation = useCallback(
    (search: string, searchItems?: any[] | null) => {
      const normalized = normalizeSearch(search);
      const liveSearch = typeof window !== 'undefined' ? window.location.search || normalized : normalized;

      if (searchHasDemoTour(liveSearch) || searchHasDemoTour(normalized)) {
        pendingProgrammaticSearch = null;
        const stripped = stripDemoTourParams(searchHasDemoTour(liveSearch) ? liveSearch : normalized);
        const title = buildSearchTabTitle(searchItems, stripped);
        const existing = findReusableTab(stripped);
        if (existing) {
          activateTab(existing.id);
          return;
        }

        const state = useSearchTabsStore.getState();
        if (state.tabs.length === 0 || !isSearchTabsEnabled) {
          const id = replaceActiveTab({ search: stripped, title });
          rememberSearchDemoTabId(id);
          return;
        }

        if (state.tabs.length < state.maxTabs) {
          const opened = openTab({ search: stripped, title });
          if (opened.ok) rememberSearchDemoTabId(opened.id);
        }
        return;
      }

      if (pendingProgrammaticSearch != null) {
        if (normalized === pendingProgrammaticSearch) {
          pendingProgrammaticSearch = null;
        }
        return;
      }

      flushActiveSearch(liveSearch);
      const title = buildSearchTabTitle(searchItems, liveSearch);
      syncActiveFromUrl(liveSearch, title);
    },
    [activateTab, flushActiveSearch, openTab, replaceActiveTab, syncActiveFromUrl],
  );

  const isSwitchingTab = useCallback(() => pendingProgrammaticSearch != null, []);

  return {
    navigateSearch,
    switchToTab,
    closeSearchTab,
    closeAllSearchTabs,
    openAllResultsSearch,
    duplicateSearchTab,
    reorderSearchTabs,
    syncFromLocation,
    flushActiveSearch,
    isSwitchingTab,
    tabs,
    activeTabId,
    maxTabs,
    canOpenTab: tabs.length < maxTabs,
  };
}

export function isNewTabModifierClick(evt: { metaKey?: boolean; ctrlKey?: boolean }): boolean {
  return Boolean(evt.metaKey || evt.ctrlKey);
}

export function isSearchWorkspacePath(pathname: string): boolean {
  return pathname === '/search' || pathname === '/examples';
}

export function closeCreatedSearchDemoTab() {
  const id = takeSearchDemoTabId();
  if (!id) return;
  const { tabs, closeTab } = useSearchTabsStore.getState();
  if (tabs.some(tab => tab.id === id)) {
    closeTab(id);
  }
}
